import { useEffect, useRef, useState } from "react";
import { User, Users } from "lucide-react";
import Analytics from "../../../../utils/Analytics";
import localization from "../../../../assets/localization";

/**
 * The tappable party-size pills that ride on the turn where the assistant asks
 * how many people are travelling ("in quanti siete?"). dengine decides WHICH
 * turn carries them and the backend decides WHICH sizes, measured per hotel and
 * per month; this only renders them.
 *
 * WHY THE COPY IS BUILT FROM `party` AND NOT FROM `label`. The teaser pills
 * above the launcher send their own visible label to the model, emoji included
 * ("Preventivo in 30 secondi ⚡"), which is a defect this deliberately does not
 * repeat: the number is the contract, the words are local. That also keeps a
 * per-month recomputed string out of the translation pipeline — pills are
 * recomputed every 12 hours and would otherwise need translating every time.
 * `label`/`message` from the server are the Italian fallback, used only if a
 * locale is missing the key.
 *
 * WHY THERE IS NO "hide" PROP. The pills belong to one reply and disappear as
 * soon as the conversation moves: ChatMessages renders them on the LAST message
 * only, so tapping (which appends the guest's own message) or typing something
 * else both retire them without any state to keep in sync. The local `sent`
 * guard is only there to stop a double tap from sending twice before React has
 * re-rendered.
 */
export default function QuotePills({
  pills,
  language = "it",
  onPillClick,
}: {
  pills: any[];
  language?: string;
  onPillClick: (message: string) => void;
}) {
  const [sent, setSent] = useState(false);
  const copy = localization.get(language)?.quote_pills;

  // One exposure event per rendered set, so `quote_pill_click / quote_pills_shown`
  // is comparable with the teaser's own funnel. The ref survives the re-render
  // that the tap itself causes.
  const trackedRef = useRef(false);
  useEffect(() => {
    if (trackedRef.current || !pills?.length) return;
    trackedRef.current = true;
    Analytics.track("quote_pills_shown", {
      parties: pills.map((p: any) => p?.party).filter((n: any) => !!n),
    });
  }, [pills]);

  if (!pills?.length) return null;

  /**
   * `label` is the accessible name and the fallback text; `count` is what the
   * chip actually shows.
   *
   * THE CHIP SHOWS A NUMBER AND AN ICON, NOT THE SENTENCE. The bot bubble is
   * `calc(100% - 48px)` of a ~380px widget less its own padding, about 284px of
   * usable width. Three chips reading "Siamo in 2" need ~106px each and three
   * reading "2 persone" need ~97px: both wrap 2 + 1, which does not look like a
   * choice, it looks like an accident. Rendered and measured on the four brands
   * in the fleet before settling here. The person glyph is what makes the bare
   * number safe — without it "2 3 4" reads as pagination, with it the number is
   * obviously an amount of people.
   *
   * The accessible name stays the whole sentence, so a screen reader announces
   * the action rather than a digit, and it is also the message the tap sends.
   */
  const textFor = (pill: any) => {
    const party = pill?.party;
    if (!copy || typeof party !== "number") {
      return {
        label: pill?.label ?? "",
        message: pill?.message ?? "",
        count: "",
      };
    }
    if (party === 1) {
      return { label: copy.alone, message: copy.alone_message, count: "1" };
    }
    return {
      label: copy.party.replace("{n}", String(party)),
      message: copy.party_message.replace("{n}", String(party)),
      count: String(party),
    };
  };

  return (
    <div className="quote-pills">
      {pills.map((pill: any, index: number) => {
        const { label, message, count } = textFor(pill);
        const Glyph = pill?.party === 1 ? User : Users;
        if (!label || !message) return null;
        return (
          <button
            key={index}
            type="button"
            className="quote-pill"
            // Drives the staggered entrance from CSS
            // (`animation-delay: calc(var(--i) * 70ms)`), so three chips read as
            // one gesture arriving rather than three boxes that were always
            // there. A CSS variable rather than an inline delay, so
            // prefers-reduced-motion cancels the whole thing in one rule.
            style={{ ["--i" as string]: index } as React.CSSProperties}
            // The chip reads "2" visually, but the accessible name is the whole
            // sentence the tap will send, so a screen reader announces the
            // action rather than a loose digit.
            aria-label={label}
            disabled={sent}
            onClick={() => {
              if (sent) return;
              setSent(true);
              Analytics.track("quote_pill_click", {
                party: pill?.party,
                position: index,
              });
              onPillClick(message);
            }}
          >
            {count ? (
              <>
                <Glyph
                  className="quote-pill-icon"
                  size={14}
                  strokeWidth={2.4}
                  aria-hidden="true"
                />
                {count}
              </>
            ) : (
              label
            )}
          </button>
        );
      })}
    </div>
  );
}
